import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FranchiseForm from '../components/FranchiseForm';

const EASE = [0.4, 0, 0.2, 1] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: EASE, delay },
  }),
};

const fadeIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: (delay = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: EASE, delay },
  }),
};

const stats = [
  { icon: '🍵', value: '10+', label: 'Products' },
  { icon: '🏪', value: '50+', label: 'Outlets' },
  { icon: '👥', value: '10K+', label: 'Customers' },
  { icon: '✅', value: '100%', label: 'Satisfaction' },
];

const About: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div>
      <Helmet>
        <title>About Us | Aswad Amruttulya - Our Story, Vision & Mission</title>
        <meta name="description" content="Learn about Aswad Amruttulya, founded by Mahadev Birajdar. A premium tea brand committed to authentic flavors, quality, and franchise partner success across India." />
        <meta property="og:title" content="About Aswad Amruttulya - Our Story" />
        <meta property="og:description" content="Founded by Mahadev Birajdar with a passion for authentic Indian tea. Discover our vision, mission, and core values." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://aswadamruttulya.com/about" />
      </Helmet>
      <Header />

      {/* ── HERO ── */}
      <section className="about-hero-section">
        <div className="container about-hero-inner">
          <motion.span
            className="about-badge"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            {t('aboutHeading')}
          </motion.span>
          <motion.h1
            className="about-hero-title"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.12}
          >
            {t('aboutTitle')}
          </motion.h1>
          <motion.p
            className="about-hero-desc"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.24}
          >
            {t('aboutDescription')}
          </motion.p>

          {/* Stats Row */}
          <motion.div
            className="about-stats-row"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.36}
          >
            {stats.map((s, i) => (
              <div className="about-stat-item" key={i}>
                <span className="about-stat-icon">{s.icon}</span>
                <span className="about-stat-value">{s.value}</span>
                <span className="about-stat-label">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="page-section about-page">
        <div className="container">

          {/* ── BRAND STORY ── */}
          <motion.div
            className="about-brand-story"
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            <div className="about-brand-story-accent" />
            <div className="about-brand-story-content">
              <span className="about-section-tag">Brand Story</span>
              <h2 className="about-brand-story-title">{t('brandTitle')}</h2>
              <p className="about-brand-story-text">{t('brandFullDescription')}</p>
            </div>
            <div className="about-brand-story-quote">
              <span className="about-brand-big-quote">"</span>
              <p>{t('aboutTagline')}</p>
            </div>
          </motion.div>

          {/* ── FOUNDER SECTION ── */}
          <motion.div
            className="about-founder-wrap"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            <div className="about-founder-label">
              <span className="about-section-tag">{t('founderHeading')}</span>
              <h2 className="about-founder-heading">{t('founderTitle')}</h2>
            </div>
            <div className="about-founder-card">
              <div className="about-founder-avatar">
                <span>MB</span>
              </div>
              <div className="about-founder-body">
                <h3 className="about-founder-name">{t('founderName')}</h3>
                <div className="about-founder-divider" />
                <p className="founder-story">{t('founderStory')}</p>
              </div>
            </div>
          </motion.div>

          {/* ── VISION & MISSION ── */}
          <div className="about-vm-section">
            <motion.div
              className="about-vm-card about-vm-vision"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
            >
              <div className="about-vm-icon">💫</div>
              <h3 className="about-vm-title">{t('vision')}</h3>
              <p className="about-vm-text">{t('visionText')}</p>
            </motion.div>
            <motion.div
              className="about-vm-card about-vm-mission"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.12}
            >
              <div className="about-vm-icon">🎯</div>
              <h3 className="about-vm-title">{t('mission')}</h3>
              <p className="about-vm-text">{t('missionText')}</p>
            </motion.div>
          </div>

          {/* ── CORE PILLARS ── */}
          <div className="about-pillars-section">
            <motion.div
              className="about-pillars-head"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <span className="about-section-tag">What We Stand For</span>
              <h2>{t('corePillarsHeading')}</h2>
            </motion.div>
            <div className="about-pillars-grid">
              {[
                { num: '01', title: t('corePillar1Title'), desc: t('corePillar1Desc'), icon: '⭐' },
                { num: '02', title: t('corePillar2Title'), desc: t('corePillar2Desc'), icon: '🚀' },
                { num: '03', title: t('corePillar3Title'), desc: t('corePillar3Desc'), icon: '🌿' },
                { num: '04', title: t('corePillar4Title'), desc: t('corePillar4Desc'), icon: '🤝' },
              ].map((p, i) => (
                <motion.div
                  className="about-pillar-card"
                  key={p.num}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i * 0.1}
                  whileHover={{ y: -8, transition: { duration: 0.25 } }}
                >
                  <div className="about-pillar-top">
                    <span className="about-pillar-icon">{p.icon}</span>
                    <span className="about-pillar-num">{p.num}</span>
                  </div>
                  <h4 className="about-pillar-title">{p.title}</h4>
                  <p className="about-pillar-desc">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ── CLOSING TAGLINE ── */}
          <motion.div
            className="about-closing-tagline"
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="about-tagline-inner">
              <div className="about-tagline-deco">
                <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Saucer */}
                  <ellipse cx="36" cy="62" rx="26" ry="5" fill="#c0855a" opacity="0.35" />
                  <ellipse cx="36" cy="60" rx="22" ry="3.5" fill="#8B3A3A" opacity="0.5" />
                  {/* Cup body */}
                  <path d="M14 28 Q13 52 36 54 Q59 52 58 28 Z" fill="url(#cupGrad)" />
                  {/* Cup rim */}
                  <ellipse cx="36" cy="28" rx="22" ry="4.5" fill="#c0855a" />
                  <ellipse cx="36" cy="28" rx="20" ry="3.2" fill="#d4956b" />
                  {/* Tea surface */}
                  <ellipse cx="36" cy="30" rx="18" ry="3" fill="#7a3010" opacity="0.85" />
                  <ellipse cx="36" cy="30" rx="14" ry="1.8" fill="#9b4a1a" opacity="0.6" />
                  {/* Handle */}
                  <path d="M57 33 Q70 33 70 42 Q70 51 57 51" stroke="#8B3A3A" strokeWidth="3.5" fill="none" strokeLinecap="round" />
                  <path d="M57 36 Q65 36 65 42 Q65 48 57 48" stroke="#c0855a" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.5" />
                  {/* Steam wisps */}
                  <path d="M28 22 Q26 16 28 10 Q30 4 28 0" stroke="#8B3A3A" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.45" />
                  <path d="M36 20 Q34 13 36 7 Q38 1 36 -3" stroke="#8B3A3A" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.45" />
                  <path d="M44 22 Q42 16 44 10 Q46 4 44 0" stroke="#8B3A3A" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.45" />
                  {/* Highlight on cup */}
                  <path d="M18 32 Q17 44 20 50" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.18" />
                  <defs>
                    <linearGradient id="cupGrad" x1="14" y1="28" x2="58" y2="54" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#c0855a" />
                      <stop offset="50%" stopColor="#a05c35" />
                      <stop offset="100%" stopColor="#8B3A3A" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <blockquote>{t('aboutTagline')}</blockquote>
              <div className="about-tagline-line" />
            </div>
          </motion.div>

        </div>
      </section>
      <FranchiseForm />
      <Footer />
    </div>
  );
};

export default About;